(() => {
  if (customElements.get("open-days")) return;
  customElements.define(
    "open-days",
    class extends HTMLElement {
      connectedCallback() {
        this.editorAbort = new AbortController();
        [
          "shopify:section:load",
          "shopify:section:unload",
          "shopify:section:reorder",
        ].forEach((type) => {
          document.addEventListener(
            type,
            () => {
              clearTimeout(this.refreshTimer);
              this.refreshTimer = setTimeout(() => this.setup(), 0);
            },
            { signal: this.editorAbort.signal },
          );
        });
        this.setup();
      }
      setup() {
        this.abort?.abort();
        this.observer?.disconnect();
        this.abort = new AbortController();
        const options = { signal: this.abort.signal };
        const select = this.querySelector("[data-clinic-select]");
        const sharedBookingUrl = this.querySelector("[data-shared-booking-url]")?.dataset.sharedBookingUrl;
        const previousClinic = select?.value;
        const previousDate = this.querySelector(
          ".od-booking-dates input:checked",
        )?.value;
        const cards = [...this.querySelectorAll("[data-clinic-card]")];
        const sources = [...this.querySelectorAll("[data-select-date]")];
        if (sharedBookingUrl) {
          try {
            const base = new URL(sharedBookingUrl);
            if (base.protocol !== "https:" || base.hostname !== "booking.smilewhite.co.uk" || base.username || base.password) throw new Error("Invalid booking URL");
            const current = new URLSearchParams(location.search);
            ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((key) => {
              if (current.has(key)) base.searchParams.set(key, current.get(key));
            });
            const urlForDate = (date) => {
              const url = new URL(base);
              if (date) url.searchParams.set("date", date);
              return url.href;
            };
            this.querySelectorAll('a[href="#od-booking"]').forEach((link) => {
              link.href = urlForDate(link.dataset.selectDate);
            });
            cards.forEach((card) => {
              const button = card.querySelector("[data-select-clinic]");
              if (!button) return;
              const link = document.createElement("a");
              link.className = button.className;
              link.href = base.href;
              link.textContent = "Book a visit";
              link.setAttribute("aria-label", "Book an Open Day visit");
              button.replaceWith(link);
            });
            const dateLinks = this.querySelector("[data-direct-booking-dates]");
            if (dateLinks && sources.length) {
              dateLinks.replaceChildren();
              sources.forEach((source) => {
                const link = document.createElement("a");
                link.className = "od-button";
                link.href = urlForDate(source.dataset.selectDate);
                link.textContent = `Book ${source.dataset.dateLabel}`;
                dateLinks.append(link);
              });
            }
          } catch {
            // Leave the customer-facing booking link in place for editor correction.
          }
        }
        if (select) {
          while (select.options.length > 1) select.remove(1);
          cards.forEach((card) => {
            const option = new Option(
              card.dataset.clinicName,
              card.dataset.clinicId,
            );
            option.dataset.bookingUrl = card.dataset.bookingUrl;
            select.add(option);
          });
          if (
            [...select.options].some(
              (option) => option.value === previousClinic,
            )
          )
            select.value = previousClinic;
        }
        const dateContainer = this.querySelector("[data-booking-dates]");
        if (dateContainer) {
          dateContainer.replaceChildren();
          sources.forEach((source) => {
            const label = document.createElement("label");
            const input = document.createElement("input");
            input.type = "radio";
            input.name = "od-booking-date";
            input.value = source.dataset.selectDate;
            input.checked = input.value === previousDate || Boolean(sharedBookingUrl && !previousDate && source === sources[0]);
            input.dataset.label = source.dataset.dateLabel;
            const span = document.createElement("span");
            const strong = document.createElement("strong");
            strong.textContent = source.dataset.dateLabel;
            const small = document.createElement("small");
            small.textContent = source.dataset.dateHours;
            span.append(strong, small);
            label.append(input, span);
            dateContainer.append(label);
          });
        }
        const dates = [...this.querySelectorAll(".od-booking-dates input")];
        const empty = this.querySelector("[data-booking-empty]");
        const live = this.querySelector("[data-booking-live]");
        const frame = this.querySelector("[data-booking-frame]");
        const status = this.querySelector("[data-selection]");
        const update = () => {
          cards.forEach((card) => {
            const button = card.querySelector("[data-select-clinic]");
            if (!button) return;
            const selected =
              !sharedBookingUrl && Boolean(select?.value) &&
              button.dataset.selectClinic === select.value;
            button.setAttribute("aria-pressed", String(selected));
            if (sharedBookingUrl) {
              button.removeAttribute("aria-pressed");
              button.setAttribute("aria-label", "Book an Open Day visit");
            }
            button.textContent = selected
              ? button.dataset.selectedLabel
              : sharedBookingUrl ? "Book a visit" : button.dataset.defaultLabel;
          });
          if (!select || !frame || !status || !live || !empty) return;
          const clinic = select.selectedOptions[0];
          const date = dates.find((input) => input.checked);
          status.textContent = sharedBookingUrl
            ? (date ? `${date.dataset.label} · Choose your practice in the booking form below.` : "Choose a date to continue.")
            : select.value && date
              ? `${clinic.textContent} · ${date.dataset.label}`
              : status.dataset.prompt;
          let url;
          const bookingUrl = sharedBookingUrl || (select.value && clinic.dataset.bookingUrl);
          if (date && bookingUrl) {
            try {
              const candidate = new URL(bookingUrl);
              if (
                candidate.protocol === "https:" &&
                candidate.hostname === "booking.smilewhite.co.uk" &&
                !candidate.username &&
                !candidate.password
              ) {
                candidate.searchParams.set("date", date.value);
                const current = new URLSearchParams(location.search);
                [
                  "utm_source",
                  "utm_medium",
                  "utm_campaign",
                  "utm_content",
                  "utm_term",
                ].forEach((key) => {
                  if (current.has(key))
                    candidate.searchParams.set(key, current.get(key));
                });
                url = candidate.href;
              }
            } catch {
              /* Invalid editor-supplied URLs remain unloaded. */
            }
          }
          live.hidden = !url;
          empty.hidden = Boolean(url) || Boolean(select.value && date);
          const fallback = this.querySelector("[data-booking-fallback]");
          if (url) {
            if (frame.getAttribute("src") !== url) frame.src = url;
            fallback.href = url;
          } else {
            frame.removeAttribute("src");
            fallback.removeAttribute("href");
          }
        };
        this.addEventListener("change", update, options);
        this.addEventListener(
          "click",
          (event) => {
            const clinic = event.target.closest("[data-select-clinic]");
            const date = event.target.closest("[data-select-date]");
            if (clinic && select) {
              select.value = clinic.dataset.selectClinic;
              update();
              this.querySelector(".od-booking-section")?.scrollIntoView({
                behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
                  ? "instant"
                  : "smooth",
              });
              if (sharedBookingUrl) frame?.focus({ preventScroll: true });
              else select.focus({ preventScroll: true });
            }
            if (date) {
              dates.forEach((input) => {
                input.checked = input.value === date.dataset.selectDate;
              });
              update();
            }
          },
          options,
        );
        const search = this.querySelector("[data-clinic-search]");
        const filter = () => {
          const query = (search?.value || "")
            .toLocaleLowerCase()
            .trim()
            .replace(/\s+/g, "");
          let count = 0;
          cards.forEach((card) => {
            card.hidden = !card.dataset.search
              .toLocaleLowerCase()
              .replace(/\s+/g, "")
              .includes(query);
            if (!card.hidden) count++;
          });
          const countLabel = this.querySelector("[data-clinic-count]");
          if (countLabel)
            countLabel.textContent = `${count} ${count === 1 ? countLabel.dataset.singular : countLabel.dataset.plural}`;
          const noResults = this.querySelector("[data-no-clinics]");
          if (noResults) noResults.hidden = count > 0;
        };
        search?.addEventListener("input", filter, options);
        filter();
        const sticky = this.querySelector(".od-mobile-sticky");
        if (sticky) {
          const visible = new Map();
          this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) =>
              visible.set(entry.target, entry.isIntersecting),
            );
            sticky.classList.toggle(
              "is-hidden",
              [...visible.values()].some(Boolean),
            );
          });
          this.querySelectorAll(".od-booking-section,.booking-experience--modal,.od-final").forEach(
            (section) => this.observer.observe(section),
          );
          const footer = this.ownerDocument.querySelector("body > footer");
          if (footer) this.observer.observe(footer);
        }
        update();
      }
      disconnectedCallback() {
        clearTimeout(this.refreshTimer);
        this.abort?.abort();
        this.editorAbort?.abort();
        this.observer?.disconnect();
      }
    },
  );
})();
